import { OfferAccess, UserRequestStatus } from "types/enums";

export type OfferAccessGateState =
    | "granted"
    | "restricted-pending"
    | "restricted-locked"
    | "private-blocked";

interface OfferAccessGateParams {
    access: OfferAccess;
    affiliates?: Array<{ id: string }>;
    userRequests?: Array<{ user_id: string; status: UserRequestStatus; created_at: string }>;
    currentUserId?: string;
    hasUnrestrictedPermission: boolean;
}

/**
 * Shared by OffersTable (icon/button in the access column) and the offer detail page
 * (full-page lock/pending/private card). Offer `status` (enabled/disabled) is not an input
 * here: the BE already excludes disabled offers from index/show for non-admins, so a
 * disabled offer never reaches this gate on the FE.
 */
export const getOfferAccessGateState = ({
    access,
    affiliates,
    userRequests,
    currentUserId,
    hasUnrestrictedPermission,
}: OfferAccessGateParams): OfferAccessGateState => {
    if (hasUnrestrictedPermission || access === OfferAccess.Public) {
        return "granted";
    }

    const isAffiliateInOffer = affiliates?.some((affiliate) => affiliate.id === currentUserId);
    if (isAffiliateInOffer) {
        return "granted";
    }

    if (access === OfferAccess.Private) {
        return "private-blocked";
    }

    const lastRequest = userRequests
        ?.filter((request) => request.user_id === currentUserId)
        ?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

    return lastRequest?.status === UserRequestStatus.Pending ? "restricted-pending" : "restricted-locked";
};
